import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Progress } from '../../ui/progress';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { auth } from '../../../lib/firebase';
import { useToast } from '../../../hooks/use-toast';
import { buildVolumeTrendModel } from '../../../lib/chartModels';
import { tenantHeaders } from '../../../lib/tenantSession';
import {
  TrendingUp, Users, Target, BarChart3, Bot, Phone, Star,
  Clock, MessageSquare, RefreshCw, AlertTriangle, CheckCircle, Zap
} from 'lucide-react';

const ExecutiveStrategicDashboard = () => {
  const { currentBusiness } = useBusiness();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [bizData, setBizData] = useState(null);
  const [execData, setExecData] = useState(null);
  const [voiceData, setVoiceData] = useState(null);
  const [chatData, setChatData] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_BASE || '';

  const fetchAll = useCallback(async () => {
    const businessId = currentBusiness?.brandId;
    if (!businessId) { setLoading(false); return; }
    setLoading(true);
    try {
      // Live Firebase ID token (auto-refreshed); cached token only as fallback (stale after ~1h → 401).
      const token = (auth.currentUser ? await auth.currentUser.getIdToken() : null) || localStorage.getItem('crm_access_token') || '';
      const h = { Authorization: `Bearer ${token}`, ...tenantHeaders() };
      const [b, e, v, c] = await Promise.allSettled([
        fetch(`${backendUrl}/api/v1/analytics/business/${businessId}?period=${selectedPeriod}`, { headers: h }),
        fetch(`${backendUrl}/api/v1/analytics/executive/${businessId}`, { headers: h }),
        fetch(`${backendUrl}/api/v1/ai-voice/business-stats/${businessId}`, { headers: h }),
        fetch(`${backendUrl}/api/v1/ai-chat/analytics/chat-metrics?business_id=${businessId}`, { headers: h }),
      ]);
      if (b.status === 'fulfilled' && b.value.ok) setBizData(await b.value.json());
      if (e.status === 'fulfilled' && e.value.ok) setExecData(await e.value.json());
      if (v.status === 'fulfilled' && v.value.ok) setVoiceData(await v.value.json());
      if (c.status === 'fulfilled' && c.value.ok) setChatData(await c.value.json());
    } catch (err) {
      toast({ title: t('common.error'), description: t('rd.errLoadStrategic'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [currentBusiness?.brandId, selectedPeriod, backendUrl]);

  useEffect(() => {
    if (currentBusiness?.brandId) fetchAll();
    else setLoading(false);
  }, [currentBusiness?.brandId, selectedPeriod]);

  const fmt = (v, suffix = '') => v != null ? `${v}${suffix}` : '—';
  const fmtRate = (v) => v != null ? `${v}%` : '—';
  const fmtCsat = (v) => v != null ? `${v}/5` : '—';

  // Daily metrics trend (pure model in lib/chartModels, unit-tested).
  const resolutionTrend = buildVolumeTrendModel(bizData?.daily_metrics);

  if (loading) {
    return (
      <div className="flex-1 h-full flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('nav.strategicDashboard')}</h1>
          <p className="text-gray-600">{t('rd.operationalPerformance')} — {currentBusiness?.name || t('rd.yourBusiness')}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">{t('rd.days7')}</SelectItem>
              <SelectItem value="30d">{t('rd.days30')}</SelectItem>
              <SelectItem value="90d">{t('rd.days90')}</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchAll} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      {/* Top-line strategic KPIs — all real */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('rd.totalCustomers')}</p>
                <p className="text-3xl font-bold text-blue-600">{fmt(execData?.total_customers)}</p>
                <p className="text-xs text-gray-400 mt-1">{t('rd.registeredInPlatform')}</p>
              </div>
              <Users className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('rd.aiResolutionRate')}</p>
                <p className="text-3xl font-bold text-green-600">{fmtRate(bizData?.ai_resolution_rate)}</p>
                <p className="text-xs text-gray-400 mt-1">{t('rd.chatAutoResolved')}</p>
              </div>
              <Bot className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('rd.customerCsat')}</p>
                <p className="text-3xl font-bold text-purple-600">{fmtCsat(bizData?.customer_satisfaction)}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {bizData?.csat_response_count ? t('rd.responsesCount', { count: bizData.csat_response_count }) : t('rd.noRatingsYet')}
                </p>
              </div>
              <Star className="h-8 w-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('rd.ticketEscalationRate')}</p>
                <p className="text-3xl font-bold text-orange-600">{fmtRate(bizData?.escalation_rate)}</p>
                <p className="text-xs text-gray-400 mt-1">{t('rd.requiresHumanAgent')}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="resolution_trend" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="resolution_trend">{t('rd.resolutionTrend')}</TabsTrigger>
          <TabsTrigger value="operational">{t('rd.operationalKpis')}</TabsTrigger>
          <TabsTrigger value="voice">{t('rd.voiceChannel')}</TabsTrigger>
          <TabsTrigger value="chat">{t('rd.chatChannel')}</TabsTrigger>
          <TabsTrigger value="service_quality">{t('rd.serviceQuality')}</TabsTrigger>
        </TabsList>

        {/* Resolution Trend Tab */}
        <TabsContent value="resolution_trend" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('rd.ticketVolumeResolutionTrend')} — {selectedPeriod}</CardTitle>
            </CardHeader>
            <CardContent>
              {resolutionTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={resolutionTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Tickets" name={t('rd.tickets')} fill="#93C5FD" />
                    <Bar dataKey="Resolved" name={t('rd.resolved')} fill="#4ADE80" />
                    <Bar dataKey="Escalated" name={t('rd.escalated')} fill="#FCA5A5" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-48 flex items-center justify-center text-gray-400">
                  {t('rd.noTicketData')}
                </div>
              )}
            </CardContent>
          </Card>
          <div className="grid md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">{t('rd.totalTickets')}</p>
                <p className="text-3xl font-bold text-blue-600">{fmt(bizData?.total_tickets)}</p>
                <p className="text-xs text-gray-400 mt-1">{t('rd.inSelectedPeriod')}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">{t('rd.resolvedTickets')}</p>
                <p className="text-3xl font-bold text-green-600">{fmt(bizData?.resolved_tickets)}</p>
                <p className="text-xs text-gray-400 mt-1">{t('rd.closedSuccessfully')}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">{t('rd.reopened')}</p>
                <p className={`text-3xl font-bold ${(bizData?.reopened_tickets || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {fmt(bizData?.reopened_tickets ?? bizData?.reopened_conversations)}
                </p>
                <p className="text-xs text-gray-400 mt-1">{t('rd.requiredFollowup')}</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Operational KPIs Tab */}
        <TabsContent value="operational" className="space-y-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { label: t('rd.avgResolutionTime'), value: bizData?.avg_resolution_time != null ? `${bizData.avg_resolution_time}h` : '—', icon: Clock, color: 'text-indigo-600', note: t('rd.createToClose') },
              { label: t('rd.avgFirstResponse'), value: bizData?.avg_first_response_time != null ? `${bizData.avg_first_response_time}m` : '—', icon: Zap, color: 'text-yellow-600', note: t('rd.firstReplyTime') },
              { label: t('rd.avgChatDuration'), value: bizData?.avg_chat_duration != null ? `${bizData.avg_chat_duration}m` : '—', icon: MessageSquare, color: 'text-teal-600', note: t('rd.perResolvedConversation') },
              { label: t('rd.openTickets'), value: fmt(execData?.active_tickets), icon: BarChart3, color: 'text-blue-600', note: t('rd.openRightNow') },
              { label: t('rd.callbackRate'), value: fmtRate(bizData?.callback_rate), icon: Phone, color: 'text-orange-600', note: t('rd.pendingCallbacks') },
              { label: t('rd.openaiUsageRate'), value: fmtRate(bizData?.openai_usage_rate), icon: Bot, color: 'text-purple-600', note: t('rd.gptFallbackRate') },
            ].map((item, i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">{item.label}</p>
                      <p className={`text-2xl font-bold ${item.color}`}>{item.value}</p>
                      <p className="text-xs text-gray-400 mt-1">{item.note}</p>
                    </div>
                    <item.icon className={`h-8 w-8 ${item.color} opacity-70`} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Voice Channel Tab */}
        <TabsContent value="voice" className="space-y-6">
          {voiceData ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { label: t('rd.totalVoiceCalls'), value: fmt(voiceData.total_calls), color: 'text-blue-600' },
                { label: t('rd.aiHandled'), value: fmt(voiceData.ai_handled), color: 'text-green-600' },
                { label: t('rd.aiResolved'), value: fmt(voiceData.ai_resolved), color: 'text-emerald-600' },
                { label: t('rd.escalated'), value: fmt(voiceData.human_escalated), color: 'text-orange-600' },
                { label: t('rd.escalationRate'), value: voiceData.escalation_rate != null ? `${voiceData.escalation_rate}%` : '—', color: 'text-red-600' },
                { label: t('rd.avgCallDuration'), value: voiceData.avg_call_duration || '—', color: 'text-purple-600' },
                { label: t('rd.pendingCallbacks'), value: fmt(voiceData.pending_callbacks), color: 'text-yellow-600' },
                { label: t('rd.overdueCallbacks'), value: fmt(voiceData.overdue_callbacks), color: 'text-red-700' },
                { label: t('rd.callbackSlaBreaches'), value: fmt(voiceData.callback_sla_breaches), color: 'text-red-800' },
              ].map((item, i) => (
                <Card key={i}>
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-500">{item.label}</p>
                    <p className={`text-2xl font-bold ${item.color}`}>{item.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400">
              {t('rd.noVoiceChannelData')}
            </div>
          )}
        </TabsContent>

        {/* Chat Channel Tab */}
        <TabsContent value="chat" className="space-y-6">
          {chatData ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                // Field names must match the authoritative chat-metrics response shape
                // (auto_resolved_count / escalated_count / auto_resolution_rate / average_confidence),
                // NOT the previously-wrong auto_resolved / escalated_to_human / resolution_rate / avg_confidence
                // which resolved to undefined and rendered '—' even when real chat data existed.
                { label: t('rd.totalChatSessions'), value: fmt(chatData.total_conversations), color: 'text-blue-600' },
                { label: t('rd.aiResolved'), value: fmt(chatData.auto_resolved_count), color: 'text-green-600' },
                { label: t('rd.escalated'), value: fmt(chatData.escalated_count), color: 'text-orange-600' },
                { label: t('rd.resolutionRate'), value: chatData.auto_resolution_rate != null ? `${chatData.auto_resolution_rate}%` : '—', color: 'text-emerald-600' },
                { label: t('rd.avgConfidence'), value: chatData.average_confidence != null ? `${(chatData.average_confidence * 100).toFixed(0)}%` : '—', color: 'text-purple-600' },
                { label: t('rd.topLanguage'), value: (chatData.language_distribution && Object.keys(chatData.language_distribution).length)
                    ? Object.entries(chatData.language_distribution).sort((a, b) => b[1] - a[1])[0][0]
                    : '—', color: 'text-teal-600' },
              ].map((item, i) => (
                <Card key={i}>
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-500">{item.label}</p>
                    <p className={`text-2xl font-bold ${item.color}`}>{item.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400">
              {t('rd.noChatData')}
            </div>
          )}
        </TabsContent>

        {/* Service Quality Tab */}
        <TabsContent value="service_quality" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle>{t('rd.csatScore')}</CardTitle></CardHeader>
              <CardContent>
                <p className="text-4xl font-bold text-purple-600 mb-2">
                  {fmtCsat(bizData?.customer_satisfaction)}
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  {bizData?.csat_response_count
                    ? t('rd.basedOnRatings', { count: bizData.csat_response_count })
                    : t('rd.noCustomerRatings')}
                </p>
                {bizData?.customer_satisfaction != null && (
                  <Progress
                    value={(bizData.customer_satisfaction / 5) * 100}
                    className="h-3"
                  />
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>{t('rd.escalationCallbacks')}</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t('rd.escalationRate')}</span>
                  <Badge variant={bizData?.escalation_rate > 30 ? 'destructive' : 'default'}>
                    {fmtRate(bizData?.escalation_rate)}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t('rd.callbackRate')}</span>
                  <Badge variant="outline">{fmtRate(bizData?.callback_rate)}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t('rd.reopenedTickets')}</span>
                  <Badge variant={(bizData?.reopened_tickets || 0) > 0 ? 'destructive' : 'default'}>
                    {fmt(bizData?.reopened_tickets ?? 0)}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
          {bizData?.operator_escalation_counts?.length > 0 && (
            <Card>
              <CardHeader><CardTitle>{t('rd.operatorEscalationDistribution')}</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {bizData.operator_escalation_counts.slice(0, 8).map((item, i) => (
                    <div key={i} className="flex items-center justify-between py-1 border-b last:border-0">
                      {/* DR-008: show the backend-enriched operator NAME, never the raw staff_id UUID. */}
                      <span className="text-sm text-gray-700 truncate flex-1">{item.operator_name || 'Unknown staff member'}</span>
                      <Badge variant="outline">{t('rd.escalationsCount', { count: item.count })}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ExecutiveStrategicDashboard;
